import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { player } from "./playerStore";

const LOOKS = {
  uomo: { skin: "#e0b48f", hair: "#3b2a20", top: "#2f6f8f", bottom: "#2b3340", shoes: "#1f2328", shoulder: 0.22, hip: 0.15, longHair: false },
  donna: { skin: "#e8bf9c", hair: "#5a3a26", top: "#b4577a", bottom: "#3a3f52", shoes: "#2a2226", shoulder: 0.18, hip: 0.17, longHair: true },
};

function Limb({ position, len, r, color, limbRef }) {
  return (
    <group ref={limbRef} position={position}>
      <mesh position={[0, -len / 2, 0]}><capsuleGeometry args={[r, len - r * 2, 6, 12]} /><meshStandardMaterial color={color} roughness={0.75} /></mesh>
    </group>
  );
}

export function UserAvatar({ gender = "uomo", state = player }) {
  const g = LOOKS[gender] || LOOKS.uomo;
  const root = useRef();
  const legL = useRef();
  const legR = useRef();
  const armL = useRef();
  const armR = useRef();
  const phase = useRef(0);
  const last = useRef({ x: state.x, z: state.z });

  useFrame((_, dt) => {
    const d = Math.min(dt, 0.05);
    const speed = Math.hypot(state.x - last.current.x, state.z - last.current.z) / (d || 1);
    last.current = { x: state.x, z: state.z };
    const moving = speed > 0.3;
    phase.current += moving ? d * Math.min(speed, 5) * 2.2 : 0;
    const sit = !!state.sit;
    const swing = moving && !sit ? Math.sin(phase.current) * 0.6 : 0;
    const ease = (ref, v) => { if (ref.current) ref.current.rotation.x += (v - ref.current.rotation.x) * Math.min(1, d * 12); };
    ease(legL, sit ? 1.45 : swing);
    ease(legR, sit ? 1.45 : -swing);
    ease(armL, sit ? 0.5 : -swing * 0.8);
    ease(armR, sit ? 0.5 : swing * 0.8);
    if (root.current) {
      root.current.position.set(state.x, sit ? -0.42 : moving ? Math.abs(Math.cos(phase.current)) * 0.04 : 0, state.z);
      root.current.rotation.y = state.yaw;
    }
  });

  return (
    <group ref={root}>
      <Limb limbRef={legL} position={[-g.hip / 2 - 0.02, 0.92, 0]} len={0.9} r={0.075} color={g.bottom} />
      <Limb limbRef={legR} position={[g.hip / 2 + 0.02, 0.92, 0]} len={0.9} r={0.075} color={g.bottom} />
      {[-1, 1].map((s) => <mesh key={s} position={[s * (g.hip / 2 + 0.02), 0.04, -0.05]}><boxGeometry args={[0.13, 0.08, 0.26]} /><meshStandardMaterial color={g.shoes} roughness={0.5} /></mesh>)}
      <mesh position={[0, 1.22, 0]}><capsuleGeometry args={[g.shoulder, 0.42, 6, 16]} /><meshStandardMaterial color={g.top} roughness={0.8} /></mesh>
      <Limb limbRef={armL} position={[-g.shoulder - 0.07, 1.47, 0]} len={0.66} r={0.055} color={g.top} />
      <Limb limbRef={armR} position={[g.shoulder + 0.07, 1.47, 0]} len={0.66} r={0.055} color={g.top} />
      <mesh position={[0, 1.6, 0]}><cylinderGeometry args={[0.05, 0.06, 0.1, 12]} /><meshStandardMaterial color={g.skin} roughness={0.6} /></mesh>
      <mesh position={[0, 1.76, 0]}><sphereGeometry args={[0.13, 24, 24]} /><meshStandardMaterial color={g.skin} roughness={0.55} /></mesh>
      <mesh position={[0, 1.81, 0.02]} scale={[1.05, 0.9, 1.08]}><sphereGeometry args={[0.135, 24, 24, 0, Math.PI * 2, 0, Math.PI / 2]} /><meshStandardMaterial color={g.hair} roughness={0.9} /></mesh>
      {g.longHair && <mesh position={[0, 1.62, 0.09]}><capsuleGeometry args={[0.11, 0.22, 6, 12]} /><meshStandardMaterial color={g.hair} roughness={0.9} /></mesh>}
    </group>
  );
}
